import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Users, Clock, CheckCircle, Search, RefreshCw,
  LogOut, DollarSign, AlertCircle, Loader2, 
  TrendingUp, UserCheck,
  Calendar, Mail, Phone, User,
  Trash2, ChevronRight, CreditCard, AlertTriangle, RotateCcw,
  BarChart3, MessageCircle, Lock, Activity
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

import { dashboardAPI, enrollmentAPI, paymentAPI } from '@/services/api';
import JanetLogo from '@/assets/janet-marketing-logo.png';
import { SecurityAlertsWidget } from '@/components/admin/SecurityAlertsWidget';
import { ChangePasswordModal } from '@/components/admin/ChangePasswordModal';

// Pull to Refresh Hook
const usePullToRefresh = (onRefresh: () => void) => {
  const [isPulling, setIsPulling] = useState(false);
  const touchStart = useRef(0);
  const touchEnd = useRef(0);
  
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      if (window.scrollY === 0) {
        touchStart.current = e.touches[0].clientY;
      }
    };
  
    const handleTouchMove = (e: TouchEvent) => {
      if (window.scrollY === 0 && touchStart.current > 0) {
        touchEnd.current = e.touches[0].clientY;
        const pullDistance = touchEnd.current - touchStart.current;
        
        if (pullDistance > 80) {
          setIsPulling(true);
        }
      }
    };
    
    const handleTouchEnd = () => {
      if (isPulling) {
        onRefresh();
      }
      setIsPulling(false);
      touchStart.current = 0;
      touchEnd.current = 0;
    };
    
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isPulling, onRefresh]);
  
  return isPulling;
};

    const handleMarkAsAddedToWhatsApp = async (enrollmentId: number) => {
      try {
        await enrollmentAPI.markAsAddedToWhatsApp(enrollmentId);
        
        toast({
          title: 'Success',
          description: 'Marked as added to WhatsApp group',
        });
        
        // Refresh dashboard data
        await dashboardAPI.getFullDashboard();
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error.message || 'Failed to update WhatsApp status',
          variant: 'destructive',
        });
      }
    };
export default function AdminDashboard() {
  // ✅ CRITICAL: Initialize all hooks at the top
  const navigate = useNavigate();
  const { logout, username, role } = useAuth();
  const { toast } = useToast();
  
  // State declarations
  const [activeTab, setActiveTab] = useState("enrollments");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("ALL");
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [statsCollapsed, setStatsCollapsed] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null);
  const [retryingPayment, setRetryingPayment] = useState<number | null>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async (silent = false) => {
    if (!silent) setIsRefreshing(true);
    
    try {
      const data = await dashboardAPI.getFullDashboard();
      setDashboardData(data);
      setLastRefresh(new Date());
    } catch (err: any) {
      console.error('Failed to fetch dashboard data:', err);
      
      toast({
        title: "Error",
        description: err.message || "Failed to load dashboard data",
        variant: "destructive",
      });
      
      if (err.status === 401) {
        logout();
        navigate('/admin');
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [logout, navigate, toast]);

  // Initial load
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Pull to refresh
  const isPulling = usePullToRefresh(() => {
    if (activeTab === "enrollments" || activeTab === "overview") {
      fetchDashboardData();
    }
  });

  // Manual refresh
  const handleRefresh = () => {
    if (activeTab === "enrollments" || activeTab === "overview") {
      fetchDashboardData(false);
    }
  };

  // Delete enrollment
  const handleDelete = async (id: number) => {
    try {
      await enrollmentAPI.delete(id);
      
      setDashboardData((prev: any) => ({
        ...prev,
        enrollments: prev.enrollments.filter((e: any) => e.id !== id),
        stats: {
          ...prev.stats,
          totalEnrollments: prev.stats.totalEnrollments - 1
        }
      }));
      
      setDeleteConfirm(null);
      
      toast({
        title: "Success",
        description: "Enrollment deleted successfully",
      });
    } catch (err: any) {
      console.error('Delete failed:', err);
      toast({
        title: "Error",
        description: err.message || "Failed to delete enrollment",
        variant: "destructive",
      });
    }
  };

  // Retry payment
  const handleRetryPayment = async (enrollmentId: number) => {
    setRetryingPayment(enrollmentId);
    
    try {
      await paymentAPI.retry(enrollmentId);
      
      toast({
        title: "Payment Initiated",
        description: "Check your phone for the M-Pesa prompt",
      });
      
      await fetchDashboardData(true);
      
    } catch (err: any) {
      console.error('Retry failed:', err);
      toast({
        title: "Error",
        description: err.message || "Failed to retry payment",
        variant: "destructive",
      });
    } finally {
      setRetryingPayment(null);
    }
  };

  // Logout
  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  // Filter data
  const filteredEnrollments = dashboardData?.enrollments?.filter((e: any) => {
    const matchesSearch = 
      e.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.phoneNumber?.includes(searchQuery);
    const matchesStatus = statusFilter === "ALL" || e.enrollmentStatus === statusFilter;
    return matchesSearch && matchesStatus;
  }) || [];

  const filteredPayments = dashboardData?.payments?.filter((p: any) => {
    const matchesSearch = 
      p.transactionReference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.phoneNumber?.includes(searchQuery);
    const matchesStatus = paymentStatusFilter === "ALL" || p.paymentStatus === paymentStatusFilter;
    return matchesSearch && matchesStatus;
  }) || [];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString("en-KE", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const statusConfig: Record<string, any> = {
    PENDING: { color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-300" },
    COMPLETED: { color: "text-teal-600", bg: "bg-teal-50", border: "border-teal-300" },
    FAILED: { color: "text-red-600", bg: "bg-red-50", border: "border-red-300" },
    SUCCESS: { color: "text-teal-600", bg: "bg-teal-50", border: "border-teal-300" },
    INITIATED: { color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-300" },
    PAYMENT_INITIATED: { color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-300" },
  };

  if (isLoading && !dashboardData) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-turquoise-dark">
        <div className="text-center space-y-4">
          <Loader2 className="w-10 h-10 animate-spin text-white mx-auto" />
          <p className="text-white">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-turquoise-rich text-white pb-20">
      {/* Pull to Refresh Indicator */}
      {isPulling && (
        <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-4">
          <div className="bg-teal-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="text-sm font-medium">Pull to refresh...</span>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-transparent border-b border-turquoise-very-dark sticky top-0 z-40 shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <img 
                src={JanetLogo}
                alt="Janet Marketing" 
                className="w-10 h-10 rounded-lg"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <div>
                <h1 className="font-bold text-base text-gray-900">Janet Marketing</h1>
                <p className="text-xs text-gray-500">Admin Portal</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {username && (
                <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-lg">
                  <Avatar className="w-6 h-6">
                    <AvatarFallback className="bg-teal-500 text-white text-xs">
                      {username.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{username}</span>
                  
                  {/* ADD THESE BUTTONS: */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setChangePasswordOpen(true)}
                    className="h-7 px-2"
                  >
                    <Lock className="w-3 h-3" />
                  </Button>
                  
                  {role === 'SUPER_ADMIN' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/admin/management')}
                      className="h-7 px-2"
                    >
                      <Users className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              )}
              
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleRefresh} 
                disabled={isRefreshing}
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-red-600"
              >
                <LogOut className="w-4 h-4" />
              </Button>

              {/* ADD AT END OF COMPONENT: */}
              <ChangePasswordModal
                open={changePasswordOpen}
                onOpenChange={setChangePasswordOpen}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Stats Cards - Collapsible */}
      <div className="px-4 py-3 bg-white border-b">
        <button
          onClick={() => setStatsCollapsed(!statsCollapsed)}
          className="w-full flex items-center justify-between text-sm font-medium text-gray-700 mb-3"
        >
          <span>Dashboard Overview</span>
          <ChevronRight className={`w-4 h-4 transition-transform ${statsCollapsed ? '' : 'rotate-90'}`} />
        </button>
        
        {!statsCollapsed && (
          <div className="grid grid-cols-2 gap-3">
            <Card className="border-l-4 border-l-teal-500">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600">Total</p>
                    <p className="text-xl font-bold text-gray-900">{dashboardData?.stats?.totalEnrollments || 0}</p>
                    <p className="text-xs text-teal-600">+{dashboardData?.stats?.todayEnrollments || 0} today</p>
                  </div>
                  <Users className="w-6 h-6 text-teal-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600">Pending</p>
                    <p className="text-xl font-bold text-gray-900">{dashboardData?.stats?.pendingEnrollments || 0}</p>
                    <p className="text-xs text-amber-600">Awaiting</p>
                  </div>
                  <Clock className="w-6 h-6 text-amber-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600">Completed</p>
                    <p className="text-xl font-bold text-gray-900">{dashboardData?.stats?.completedEnrollments || 0}</p>
                    <p className="text-xs text-emerald-600">
                      {dashboardData?.stats?.totalEnrollments 
                        ? ((dashboardData?.stats?.completedEnrollments / dashboardData?.stats?.totalEnrollments) * 100).toFixed(0)
                        : 0}%
                    </p>
                  </div>
                  <CheckCircle className="w-6 h-6 text-emerald-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-teal-500">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600">Revenue</p>
                    <p className="text-base font-bold text-gray-900">
                      {formatCurrency(dashboardData?.stats?.totalRevenue || 0)}
                    </p>
                  </div>
                  <DollarSign className="w-6 h-6 text-teal-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="px-4 py-4">
        {/* Enrollments Tab */}
        {activeTab === "enrollments" && (
          <div className="space-y-3">
            {/* Search and Filter */}
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search enrollments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Status</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="FAILED">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Enrollments List */}
            <div className="space-y-2">
              {filteredEnrollments.map((enrollment: any) => {
                const config = statusConfig[enrollment.enrollmentStatus] || statusConfig.PENDING;
                
                // ✅ Parse selected weeks (backend sends as number[])
                const selectedWeeks = enrollment.selectedWeeks || [];
                const weeksCount = enrollment.weeksCount || selectedWeeks.length;
                
                return (
                  <Card 
                    key={enrollment.id} 
                    className="border-l-4 border-l-teal-500 cursor-pointer active:scale-98 transition-transform"
                    onClick={() => setSelectedEnrollment(enrollment)}
                  >
                    <CardContent className="p-3">
                      {/* Header: Name + Status */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 text-sm">{enrollment.name}</p>
                          <p className="text-xs text-gray-500">{enrollment.email}</p>
                        </div>
                        <Badge className={`${config.bg} ${config.color} border ${config.border} text-xs`}>
                          {enrollment.enrollmentStatus}
                        </Badge>
                      </div>

                      {/* Contact Info */}
                      <div className="flex items-center gap-3 text-xs text-gray-600 mb-2">
                        <div className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {enrollment.phoneNumber}
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(enrollment.createdAt)}
                        </div>
                      </div>

                      {/* ✅ NEW: Week Selection Display */}
                      <div className="bg-teal-50 border border-teal-200 rounded-lg p-2 mb-2">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-medium text-teal-900">
                            {weeksCount === 4 ? 'Full Course (All 4 Weeks)' : `${weeksCount} Week${weeksCount !== 1 ? 's' : ''} Selected`}
                          </p>
                          <Badge variant="outline" className="text-xs bg-white">
                            {enrollment.tutorialType}
                          </Badge>
                        </div>
                        
                        {/* Week badges */}
                        <div className="flex flex-wrap gap-1">
                          {[1, 2, 3, 4].map(weekNum => {
                            const isSelected = selectedWeeks.includes(weekNum);
                            return (
                              <div
                                key={weekNum}
                                className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  isSelected 
                                    ? 'bg-teal-500 text-white' 
                                    : 'bg-gray-200 text-gray-400'
                                }`}
                              >
                                W{weekNum}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* ✅ NEW: WhatsApp Status */}
                      <div className="flex items-center justify-between text-xs mb-2">
                        <div className="flex items-center gap-1 text-gray-600">
                          <MessageCircle className="w-3 h-3" />
                          <span>WhatsApp:</span>
                        </div>
                        {enrollment.addedToWhatsApp ? (
                          <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">
                            ✓ Added
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
                            Pending
                          </Badge>
                        )}
                      </div>

                      {/* Failure Reason */}
                      {enrollment.failureReason && (
                        <Alert className="mt-2 bg-red-50 border-red-200 py-1 px-2">
                          <AlertDescription className="text-xs text-red-700 flex items-start gap-1">
                            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>{enrollment.failureReason}</span>
                          </AlertDescription>
                        </Alert>
                      )}

                      {/* Actions */}
                      <div className="flex items-center justify-between mt-2">
                        <Badge variant="outline" className="text-xs">
                          {formatCurrency(enrollment.totalAmount)}
                        </Badge>
                        <div className="flex gap-1">
                          {/* ✅ NEW: Quick WhatsApp button */}
                          {!enrollment.addedToWhatsApp && enrollment.enrollmentStatus === 'CONFIRMED' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs text-green-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsAddedToWhatsApp(enrollment.id);
                              }}
                            >
                              <MessageCircle className="w-3 h-3 mr-1" />
                              Add to WhatsApp
                            </Button>
                          )}
                          
                          {enrollment.enrollmentStatus === 'FAILED' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRetryPayment(enrollment.id);
                              }}
                              disabled={retryingPayment === enrollment.id}
                            >
                              {retryingPayment === enrollment.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  <RotateCcw className="w-3 h-3 mr-1" />
                                  Retry
                                </>
                              )}
                            </Button>
                          )}
                          
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm(enrollment);
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {filteredEnrollments.length === 0 && (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No enrollments found</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === "payments" && (
          <div className="space-y-3">
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search payments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={paymentStatusFilter} onValueChange={setPaymentStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Payments</SelectItem>
                  <SelectItem value="SUCCESS">Success</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="FAILED">Failed</SelectItem>
                  <SelectItem value="INITIATED">Initiated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              {filteredPayments.map((payment: any) => {
                const config = statusConfig[payment.paymentStatus] || statusConfig.PENDING;
                return (
                  <Card key={payment.id} className="border-l-4 border-l-teal-500">
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-mono text-xs text-teal-600 mb-1">
                            {payment.transactionReference}
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {formatCurrency(payment.amount)}
                          </p>
                          <p className="text-xs text-gray-500">{payment.phoneNumber}</p>
                        </div>
                        <Badge className={`${config.bg} ${config.color} border ${config.border} text-xs`}>
                          {payment.paymentStatus}
                        </Badge>
                      </div>

                      {payment.mpesaReceiptNumber && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded p-2 mb-2">
                          <p className="text-xs text-emerald-700">
                            <span className="font-medium">Receipt:</span> {payment.mpesaReceiptNumber}
                          </p>
                        </div>
                      )}

                      {payment.failureReason && (
                        <Alert className="bg-red-50 border-red-200 py-1 px-2 mb-2">
                          <AlertDescription className="text-xs text-red-700 flex items-start gap-1">
                            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>{payment.failureReason}</span>
                          </AlertDescription>
                        </Alert>
                      )}

                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{formatDate(payment.createdAt)}</span>
                        {payment.completedAt && (
                          <span className="text-emerald-600">
                            ✓ {formatDate(payment.completedAt)}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {filteredPayments.length === 0 && (
                <Card>
                  <CardContent className="p-8 text-center">
                    <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No payments found</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-4">
            <SecurityAlertsWidget />
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {dashboardData?.enrollments?.slice(0, 5).map((enrollment: any, i: number) => (
                  <div key={i} className="flex items-center gap-3 pb-2 border-b last:border-0">
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <UserCheck className="w-4 h-4 text-teal-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{enrollment.name}</p>
                      <p className="text-xs text-gray-500">enrolled for {enrollment.tutorialType}</p>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{formatDate(enrollment.createdAt)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Weekly Enrollments</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={[
                    { day: 'Mon', enrollments: 12 },
                    { day: 'Tue', enrollments: 19 },
                    { day: 'Wed', enrollments: 15 },
                    { day: 'Thu', enrollments: 22 },
                    { day: 'Fri', enrollments: 18 },
                    { day: 'Sat', enrollments: 8 },
                    { day: 'Sun', enrollments: 5 },
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1f2937', 
                        border: 'none',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="enrollments" 
                      stroke="#14b8a6" 
                      strokeWidth={2}
                      dot={{ fill: '#14b8a6', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 shadow-lg">
        <div className="grid grid-cols-4 h-16">
          <button
            onClick={() => setActiveTab("enrollments")}
            className={`flex flex-col items-center justify-center gap-1 transition-colors ${
              activeTab === "enrollments" 
                ? "text-teal-600 bg-teal-50" 
                : "text-gray-600"
            }`}
          >
            <Users className="w-5 h-5" />
            <span className="text-xs font-medium">Enrollments</span>
          </button>
          
          <button
            onClick={() => setActiveTab("payments")}
            className={`flex flex-col items-center justify-center gap-1 transition-colors ${
              activeTab === "payments" 
                ? "text-teal-600 bg-teal-50" 
                : "text-gray-600"
            }`}
          >
            <CreditCard className="w-5 h-5" />
            <span className="text-xs font-medium">Payments</span>
          </button>
          
          <button
            onClick={() => setActiveTab("overview")}
            className={`flex flex-col items-center justify-center gap-1 transition-colors ${
              activeTab === "overview" 
                ? "text-teal-600 bg-teal-50" 
                : "text-gray-600"
            }`}
          >
            <BarChart3 className="w-5 h-5" />
            <span className="text-xs font-medium">Overview</span>
          </button>
          
          {/* ADD THIS NEW TAB: */}
          <button
            onClick={() => navigate('/admin/login-logs')}
            className="flex flex-col items-center justify-center gap-1 transition-colors text-gray-600 hover:text-teal-600"
          >
            <Activity className="w-5 h-5" />
            <span className="text-xs font-medium">Logs</span>
          </button>
        </div>
      </nav>

      {/* Enrollment Detail Modal */}
      <Dialog open={!!selectedEnrollment} onOpenChange={() => setSelectedEnrollment(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Enrollment Details</DialogTitle>
            <DialogDescription>
              Complete information for #{selectedEnrollment?.id}
            </DialogDescription>
          </DialogHeader>

           {selectedEnrollment && (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <Badge className={`${statusConfig[selectedEnrollment.enrollmentStatus]?.bg} ${statusConfig[selectedEnrollment.enrollmentStatus]?.color} border ${statusConfig[selectedEnrollment.enrollmentStatus]?.border}`}>
                  {selectedEnrollment.enrollmentStatus}
                </Badge>
                <span className="text-xs text-gray-500">
                  {formatDate(selectedEnrollment.createdAt)}
                </span>
              </div>

              {/* Personal Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Personal Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <Label className="text-xs text-gray-500">Full Name</Label>
                    <p className="text-sm font-medium">{selectedEnrollment.name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Email</Label>
                    <p className="text-sm font-medium flex items-center gap-1">
                      <Mail className="w-3 h-3 text-gray-400" />
                      {selectedEnrollment.email}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Phone Number</Label>
                    <p className="text-sm font-medium flex items-center gap-1">
                      <Phone className="w-3 h-3 text-gray-400" />
                      {selectedEnrollment.phoneNumber}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Tutorial Type</Label>
                    <Badge variant="outline" className="mt-1">
                      {selectedEnrollment.tutorialType}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Payment Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <Label className="text-xs text-gray-500">Transaction Reference</Label>
                    <p className="text-sm font-mono text-teal-600">
                      {selectedEnrollment.transactionReference}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Amount</Label>
                    <p className="text-sm font-medium">
                      {formatCurrency(selectedEnrollment.amount)}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Payment Status</Label>
                    <Badge className={`${statusConfig[selectedEnrollment.paymentStatus]?.bg} ${statusConfig[selectedEnrollment.paymentStatus]?.color} border ${statusConfig[selectedEnrollment.paymentStatus]?.border} mt-1`}>
                      {selectedEnrollment.paymentStatus}
                    </Badge>
                  </div>
                  
                  {selectedEnrollment.mpesaReceiptNumber && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                      <Label className="text-xs text-emerald-700">M-Pesa Receipt</Label>
                      <p className="text-sm font-mono text-emerald-900">
                        {selectedEnrollment.mpesaReceiptNumber}
                      </p>
                    </div>
                  )}

                  {selectedEnrollment.failureReason && (
                    <Alert className="bg-red-50 border-red-200">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      <AlertDescription className="text-sm text-red-700">
                        <strong>Failure Reason:</strong><br />
                        {selectedEnrollment.failureReason}
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex gap-2">
                {selectedEnrollment.enrollmentStatus === 'FAILED' && (
                  <Button
                    onClick={() => {
                      handleRetryPayment(selectedEnrollment.id);
                      setSelectedEnrollment(null);
                    }}
                    disabled={retryingPayment === selectedEnrollment.id}
                    className="flex-1 bg-teal-500 hover:bg-teal-600"
                  >
                    {retryingPayment === selectedEnrollment.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Retrying...
                      </>
                    ) : (
                      <>
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Retry Payment
                      </>
                    )}
                  </Button>
                )}
                <Button
                  variant="destructive"
                  onClick={() => {
                    setDeleteConfirm(selectedEnrollment);
                    setSelectedEnrollment(null);
                  }}
                  className="flex-1"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this enrollment? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {deleteConfirm && (
            <div className="space-y-3">
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-sm text-amber-700">
                  <strong>Warning:</strong> You are about to delete enrollment for:
                </AlertDescription>
              </Alert>
              
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <p className="text-sm font-medium">{deleteConfirm.name}</p>
                <p className="text-xs text-gray-600">{deleteConfirm.email}</p>
                <p className="text-xs text-gray-600">{deleteConfirm.phoneNumber}</p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deleteConfirm.id)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Enrollment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refreshing Indicator */}
      {isRefreshing && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-teal-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 animate-in slide-in-from-top">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="text-sm font-medium">Refreshing...</span>
          </div>
        </div>
      )}
    </div>
  );
}